/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { CalendarDate } from '@internationalized/date';

import { pluralize, SearchActions } from 'sarsaparilla';

import type { DatePickerState } from './DatePicker.types';

// Event names, labels, and the Clear Dates GTM id all mirror the legacy
// FlexDates picker in ui-navigation, so existing GA reports keep matching.

export const CLICK_TAG_LABEL = 'Flexible Booking';

/** GTM tag the legacy picker put on its "Clear Dates" button. */
export const CLEAR_DATES_GA_TRACKING_ID = '249428662827';

const TAB_CLICK_TAG_ACTIONS: Record<DatePickerState['activeTab'], string> = {
    dates: 'Choose Dates',
    flexible: "I'm Flexible!",
};

export interface ClickTagProps {
    clickTagCategory?: string;
    clickTagAction?: string;
    clickTagLabel?: string;
}

export interface ClearButtonTrackingProps extends ClickTagProps {
    gaTrackingId?: string;
    gaTrackingValue?: string;
}

/**
 * Pushes a click event for elements that aren't Sarsa buttons.
 * Does nothing without a category, matching Sarsa's clickTag props.
 */
export function trackClick(category: string | undefined, action: string) {
    if (!category) {
        return;
    }

    SearchActions.trackRunSearch({ category, action, label: CLICK_TAG_LABEL }, () => {});
}

export function getTabClickTagAction(tab: DatePickerState['activeTab']): string {
    return TAB_CLICK_TAG_ACTIONS[tab];
}

/** Formats a date like moment's `LL`, e.g. "October 20, 2026". */
function formatTagDate(date: CalendarDate): string {
    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date.toDate('UTC'));
}

function formatPlusMinus(days: number): string {
    return ` plus or minus ${days} ${pluralize(days, 'Day', 'Days')}`;
}

/**
 * Click tag for the primary action, describing the selection being applied.
 */
export function getApplyClickTag(
    state: DatePickerState,
    category: string | undefined
): ClickTagProps {
    if (!category) {
        return {};
    }

    if (state.activeTab === 'flexible') {
        const nights = state.availNights ?? 1;
        const months = state.availMonths?.length ?? 0;

        return {
            clickTagCategory: category,
            clickTagAction: `${nights} ${pluralize(nights, 'Day', 'Days')} Within ${months} Selected ${pluralize(months, 'Month', 'Months')}`,
            clickTagLabel: CLICK_TAG_LABEL,
        };
    }

    let startAction = state.startDate
        ? `Check-in: ${formatTagDate(state.startDate)}`
        : '';
    let endAction = state.endDate ? `Check-out: ${formatTagDate(state.endDate)}` : '';
    let label = 'Selected Dates';

    // "Exact dates" (0) is no range, like an unset range in the legacy picker.
    if (state.flexDays) {
        startAction += formatPlusMinus(state.flexDays);
        endAction += formatPlusMinus(state.flexDays);
        label = 'Selected Dates with plus or minus days';
    }

    return {
        clickTagCategory: category,
        clickTagAction: `${startAction}, ${endAction}`,
        clickTagLabel: label,
    };
}

/**
 * Tracking props for the in-panel clear button. The legacy picker tracked
 * each tab's clear button differently, and didn't track either on mobile.
 */
export function getClearButtonTracking(
    activeTab: DatePickerState['activeTab'],
    category: string | undefined,
    isDrawer: boolean
): ClearButtonTrackingProps {
    if (!category || isDrawer) {
        return {};
    }

    if (activeTab === 'flexible') {
        return {
            clickTagCategory: category,
            clickTagAction: 'Clear Selection',
            clickTagLabel: CLICK_TAG_LABEL,
        };
    }

    return {
        gaTrackingId: CLEAR_DATES_GA_TRACKING_ID,
        gaTrackingValue: category,
    };
}
